// The left sidebar on the autogenerate page: a scrollable list of past/active
// runs (newest first), each a button that selects the run. Handles the loading
// and empty states; selection + "new run" are delegated to the parent.

import { Loader2, Plus } from 'lucide-react'
import type { GwbkAutogenJobSummary } from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import { formatWhen, statusMeta, summaryLine } from './gwbk-autogen-status'

export interface GwbkAutogenRunListProps {
  jobs: GwbkAutogenJobSummary[]
  selectedId: string
  isLoading: boolean
  onSelect: (id: string) => void
  onNewRun: () => void
}

const GwbkAutogenRunList = ({
  jobs,
  selectedId,
  isLoading,
  onSelect,
  onNewRun
}: GwbkAutogenRunListProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-ink-3">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        Loading runs…
      </div>
    )
  }

  if (jobs.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 p-6 text-center text-sm text-ink-3">
        <p className="m-0">No runs yet.</p>
        <Button variant="outline" size="sm" onClick={onNewRun}>
          <Plus className="size-4" aria-hidden />
          Start a run
        </Button>
      </div>
    )
  }

  return (
    <ul className="flex flex-col">
      {jobs.map((j) => {
        const meta = statusMeta(j.state)
        const active = j.id === selectedId
        return (
          <li key={j.id}>
            <button
              type="button"
              onClick={() => onSelect(j.id)}
              className={`flex w-full flex-col gap-1 border-b border-line/60 px-3 py-2.5 text-left transition-colors ${
                active ? 'bg-bg-sunk' : 'hover:bg-bg-sunk/50'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-ink-1" title={j.gatewayId}>
                  {j.gatewayId || j.gatewayUrl || j.id}
                </span>
                <span
                  className={`ml-auto flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-10 ${meta.className}`}
                >
                  {meta.spin ? <Loader2 className="size-2.5 animate-spin" aria-hidden /> : null}
                  {meta.label}
                </span>
              </div>
              <span className="truncate text-11 text-ink-3">{summaryLine(j)}</span>
              <span className="text-10 text-ink-3">{formatWhen(j.createdAt)}</span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

export default GwbkAutogenRunList
